import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  InputBase,
  Popper,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  Divider
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { searchApi, SearchResult, SearchGroup } from '@/api/search';
import { NAV_MODULES, NavModule } from '@/utils/constants';
import { useAuthStore } from '@/stores/authStore';

/** 把菜单树扁平化成可搜索项，并按角色可见性过滤（逻辑同 Sidebar.isItemVisible） */
const flattenModules = (
  mods: NavModule[],
  parent: string,
  isVisible: (m: NavModule) => boolean,
  acc: { key: string; label: string; parent: string; path: string }[]
): { key: string; label: string; parent: string; path: string }[] => {
  for (const m of mods) {
    if (!isVisible(m)) continue;
    if (m.enabled && m.path) {
      acc.push({ key: `${m.key}|${m.path}`, label: m.label, parent: parent || '', path: m.path });
    }
    if (m.children && m.children.length) {
      flattenModules(m.children, m.label, isVisible, acc);
    }
  }
  return acc;
};

const GlobalSearch: React.FC = () => {
  const navigate = useNavigate();
  const currentRole = useAuthStore((s) => s.currentRole);
  const isPlatformAdmin = useAuthStore((s) => s.isPlatformAdmin);
  const isChannel = useAuthStore((s) => s.isChannel);
  const isConsultant = useAuthStore((s) => s.isConsultant);
  const identity = useAuthStore((s) => s.identity);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<SearchGroup[]>([]);
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number>();

  // Ctrl / Cmd + K 唤起搜索
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const term = q.trim();
    if (!term) {
      setGroups([]);
      setLoading(false);
      setOpen(false);
      return;
    }
    setLoading(true);
    setOpen(true);
    debounceRef.current = window.setTimeout(async () => {
      try {
        const data = await searchApi.global(term);
        setGroups(data.groups || []);
      } catch {
        setGroups([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [q]);

  // 功能模块：前端本地实时匹配（秒出、随角色过滤、无需后端）
  const isVisible = (m: NavModule): boolean => {
    if (!m.requiredRoles || m.requiredRoles.length === 0) return true;
    if (currentRole && m.requiredRoles.includes(currentRole)) return true;
    if (isConsultant && m.requiredRoles.includes('consultant')) return true;
    if (identity === 'consultant' && m.requiredRoles.includes('consultant_self')) return true;
    if (isPlatformAdmin && m.requiredRoles.includes('platform_admin')) return true;
    if (isChannel && m.requireChannel) return true;
    return false;
  };

  const moduleItems: SearchResult[] = useMemo(() => {
    const term = q.trim();
    if (!term) return [];
    const flat = flattenModules(NAV_MODULES, '', isVisible, []);
    const t = term.toLowerCase();
    return flat
      .filter((m) => m.label.toLowerCase().includes(t) || m.parent.toLowerCase().includes(t))
      .map((m) => ({
        id: m.path,
        type: 'module',
        module: '功能模块',
        label: m.parent,
        title: m.label,
        subtitle: '',
        route: m.path,
      }));
  }, [q, currentRole, isPlatformAdmin, isChannel, isConsultant, identity]);

  const moduleGroup: SearchGroup | null = moduleItems.length
    ? { module: '功能模块', items: moduleItems }
    : null;
  const allGroups = [...(moduleGroup ? [moduleGroup] : []), ...groups];
  const total = allGroups.reduce((s, g) => s + g.items.length, 0);

  const go = (item: SearchResult) => {
    setOpen(false);
    setQ('');
    if (item.route) navigate(item.route);
  };

  const showPopper = open && (loading || total > 0 || q.trim() !== '');

  return (
    <Box ref={anchorRef} sx={{ flexGrow: 1, maxWidth: 460, mx: { xs: 0, sm: 2 }, position: 'relative' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f0f2f5', borderRadius: 1, px: 1.5, height: 36 }}>
        <SearchIcon fontSize="small" sx={{ color: '#888', mr: 1 }} />
        <InputBase
          inputRef={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => q.trim() && setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setOpen(false);
              inputRef.current?.blur();
            }
          }}
          placeholder="搜索 模块 / 客户 / 顾问 / 问卷 / 项目 / 技工…"
          sx={{ flexGrow: 1, fontSize: 14 }}
        />
        <Typography variant="caption" sx={{ color: '#bbb', ml: 1, display: { xs: 'none', sm: 'block' } }}>
          Ctrl K
        </Typography>
      </Box>
      <Popper
        open={showPopper}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth || 460 }}
      >
        <Paper sx={{ mt: 0.5, width: '100%', maxHeight: 440, overflowY: 'auto', boxShadow: 3 }}>
          {loading && (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!loading && total === 0 && q.trim() !== '' && (
            <Box sx={{ p: 2, color: '#999', fontSize: 13 }}>未找到匹配「{q.trim()}」的记录</Box>
          )}
          {!loading &&
            allGroups.map((g) => (
              <Box key={g.module}>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    px: 2,
                    py: 0.5,
                    bgcolor: '#fafafa',
                    color: '#999',
                    position: 'sticky',
                    top: 0
                  }}
                >
                  {g.module}（{g.items.length}）
                </Typography>
                <List dense disablePadding>
                  {g.items.map((it) => (
                    <ListItemButton key={`${it.type}-${it.id}`} onClick={() => go(it)} disabled={!it.route}>
                      <ListItemText
                        primary={it.title}
                        secondary={it.subtitle ? `${it.label} · ${it.subtitle}` : it.label}
                        primaryTypographyProps={{ fontSize: 14 }}
                        secondaryTypographyProps={{ fontSize: 12 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
                <Divider />
              </Box>
            ))}
        </Paper>
      </Popper>
    </Box>
  );
};

export default GlobalSearch;
